import React, { useEffect, useRef, useState } from "react";
import { ViewStyle, StyleSheet, View } from "react-native";
import { Sizing } from "../../../theme";
import { MessageBubble } from "../components/MessageBubble";
import { ColorPicker } from "../components/ColorPicker";
import {
  mixActions,
  Target,
  useAppDispatch,
  useAppSelector,
} from "../../../store";
import { GradientButton, Card } from "../../../components";
import { Icons } from "../../../theme";
import { ScrollView } from "react-native-gesture-handler";
import { messageStore } from "../utils/messages";
import {
  checkContinueAllowed,
  isSameSender,
  updateMessageUI,
} from "../utils/messageUtils";
import { Popup } from "../components/Popup";

export interface MessageScreenProps {
  style?: ViewStyle;
}

export const MessageScreen = ({ style }: MessageScreenProps) => {
  const dispatch = useAppDispatch();
  const mix = useAppSelector((state) => state.mix);
  const scrollViewRef = useRef<ScrollView>(null);
  const visibleMsgCount = useAppSelector(
    (state) => state.mix.msgProgress.visibleMsgCount
  );
  const messages = messageStore(mix, dispatch);
  const popup = useAppSelector((state) => state.mix.msgProgress.popup);

  const handleNext = () => {
    if (!checkContinueAllowed(messages, messages[visibleMsgCount - 1], mix))
      return;

    // scroll to bottom (with delay ensuring new messages rendered)
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);

    if (visibleMsgCount < messages.length) {
      dispatch(
        mixActions.updateProgress({ visibleMsgCount: visibleMsgCount + 1 })
      );
      const newMsg = messages[visibleMsgCount];

      updateMessageUI(dispatch, newMsg.id);
    } else {
      // could reset view
    }
  };

  // registers handleNext() for "Enter" button
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown); // Cleanup: called when component re-rendered | destroyed
  }, [handleNext]); // when component rendered

  return (
    <Card style={[styles.chat, style]}>
      {popup && (
        <Popup
          icon={popup.icon}
          title={popup.title}
          timerDuration={popup.timerDuration}
          activateConfetti={popup.activateConfetti}
        >
          {popup.children}
        </Popup>
      )}

      <ScrollView ref={scrollViewRef} style={styles.chatScrollView}>
        {messages.slice(0, visibleMsgCount).map((msg, index) => {
          const marginTop = isSameSender(mix, index)
            ? Sizing.cs(4)
            : Sizing.cs(20);
          return (
            <MessageBubble
              key={index}
              text={msg.text}
              children={msg.content}
              target={msg.target && (msg.target as Target)}
              style={{ marginTop, ...msg.customMsgStyles }}
            />
          );
        })}
        <View style={styles.buttonRow}>
          <GradientButton
            disabled={
              visibleMsgCount === messages.length || // wenn letzte Nachricht durch id andere Bedingung hat, reicht checkContinueAllowed nicht
              !checkContinueAllowed(
                messages,
                messages[visibleMsgCount - 1],
                mix
              )
            }
            style={[styles.continueButtons]}
            outlined
            title={
              visibleMsgCount === messages.length
                ? "(Fertig)"
                : "Weiter (Enter)"
            }
            onPress={() => handleNext()}
          />
          <GradientButton
            style={[styles.continueButtons, { opacity: 0 }]}
            outlined
            onPress={() => dispatch(mixActions.reset())}
          >
            <Icons.Reload size="medium" />
          </GradientButton>
        </View>
      </ScrollView>
    </Card>
  );
};

export const GeneratorPicker = () => {
  const dispatch = useAppDispatch();

  return (
    <ColorPicker
      onChange={(color: string) => dispatch(mixActions.generate(color))}
    />
  );
};

const styles = StyleSheet.create({
  chat: {
    height: "100%",
    marginHorizontal: Sizing.cs(5),
    padding: Sizing.cs(20),
  },
  chatScrollView: {
    paddingVertical: Sizing.cs(25),
  },
  continueButtons: {
    width: "40%",
    height: Sizing.cs(60),
    marginTop: Sizing.cs(40),
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    width: "100%",
  },
});
